// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName } from '@medplum/core';
import type { DiagnosticReport, Patient, Practitioner, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { CLOUDPAT_HSAT_CODING, HSAT_ORDERED_DATE_EXTENSION_URL } from '@polarissleep/shared';
import { useCallback, useEffect, useState } from 'react';
import type { HsatDeviceEntry } from './useHSATDevices';

const DR_STATUS_LABELS: Record<DiagnosticReport['status'], string> = {
  registered: 'Registered',
  partial: 'Results Available',
  preliminary: 'Preliminary',
  corrected: 'Corrected',
  appended: 'Appended',
  cancelled: 'Cancelled',
  'entered-in-error': 'Entered in Error',
  unknown: 'Unknown',
  final: 'Completed',
  amended: 'Amended',
};

interface UseActiveHSATTestsReturn {
  entries: HsatDeviceEntry[];
  loading: boolean;
  refresh: () => void;
}

export function useActiveHSATTests(): UseActiveHSATTestsReturn {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<HsatDeviceEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const serviceRequests = (await medplum.searchResources('ServiceRequest', {
        code: `${CLOUDPAT_HSAT_CODING.system}|${CLOUDPAT_HSAT_CODING.code}`,
        _count: '1000',
      })) as ServiceRequest[];

      if (serviceRequests.length === 0) {
        setEntries([]);
        return;
      }

      const srIds = serviceRequests.map((sr) => sr.id).filter((id): id is string => Boolean(id));
      const srMap = new Map(serviceRequests.map((sr) => [sr.id ?? '', sr]));

      const diagnosticReports = (await medplum.searchResources('DiagnosticReport', {
        'based-on': srIds.map((id) => `ServiceRequest/${id}`).join(','),
        _count: '1000',
        _sort: '-_lastUpdated',
      })) as DiagnosticReport[];

      if (diagnosticReports.length === 0) {
        setEntries([]);
        return;
      }

      const patientIds = [
        ...new Set(
          diagnosticReports.map((dr) => dr.subject?.reference?.split('/')[1]).filter((id): id is string => Boolean(id))
        ),
      ];

      const practitionerIds = [
        ...new Set(
          diagnosticReports
            .map((dr) => {
              const srId = dr.basedOn?.[0]?.reference?.split('/')[1] ?? '';
              return srMap.get(srId)?.requester?.reference?.split('/')[1];
            })
            .filter((id): id is string => Boolean(id))
        ),
      ];

      const [patients, practitioners] = await Promise.all([
        patientIds.length > 0
          ? medplum.searchResources('Patient', { _id: patientIds.join(','), _count: '1000' })
          : Promise.resolve([] as Patient[]),
        practitionerIds.length > 0
          ? medplum.searchResources('Practitioner', { _id: practitionerIds.join(','), _count: '1000' })
          : Promise.resolve([] as Practitioner[]),
      ]);

      const patientMap = new Map(patients.map((p) => [p.id ?? '', p]));
      const practitionerMap = new Map(practitioners.map((p) => [p.id ?? '', p]));

      const result: HsatDeviceEntry[] = diagnosticReports.map((dr) => {
        const patientId = dr.subject?.reference?.split('/')[1] ?? '';
        const patient = patientMap.get(patientId);
        const patientName = patient?.name?.[0] ? formatHumanName(patient.name[0]) : 'Unknown Patient';

        const srId = dr.basedOn?.[0]?.reference?.split('/')[1] ?? '';
        const sr = srMap.get(srId) as ServiceRequest | undefined;

        const practitionerId = sr?.requester?.reference?.split('/')[1] ?? '';
        const practitioner = practitionerMap.get(practitionerId);
        const requesterName = practitioner?.name?.[0] ? formatHumanName(practitioner.name[0]) : undefined;

        const orderedDate = sr?.extension?.find((e) => e.url === HSAT_ORDERED_DATE_EXTENSION_URL)?.valueDateTime;

        return {
          serviceRequestId: srId || (dr.id ?? ''),
          patientId,
          patientName,
          requesterId: practitionerId || undefined,
          orderedDate,
          deviceStatus: null,
          hsatStatusDisplay: DR_STATUS_LABELS[dr.status] ?? dr.status,
          requesterName,
          serviceRequest: sr ?? ({ resourceType: 'ServiceRequest' } as ServiceRequest),
          dateArrived: undefined,
          activatedDate: undefined,
          lastContact: undefined,
          serialNumber: undefined,
        };
      });

      setEntries(result);
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  useEffect(() => {
    fetchEntries().catch(console.error);
  }, [fetchEntries]);

  return { entries, loading, refresh: fetchEntries };
}
